import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend
} from "chart.js"

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend
)

export default class extends Controller {
  static targets = ["canvas", "form", "windSpeed", "power", "tbody"]
  static values = {
    points: Array,
    profileId: Number
  }

  connect() {
    this.renderChart()
  }

  renderChart() {
    if (this.chart) {
      this.chart.destroy()
    }

    const sortedPoints = [...this.pointsValue].sort((a, b) => a.wind_speed - b.wind_speed)
    
    const ctx = this.canvasTarget.getContext("2d")
    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: sortedPoints.map(p => p.wind_speed),
        datasets: [{
          label: "Power Output (kW)",
          data: sortedPoints.map(p => p.power),
          borderColor: "rgb(34, 139, 34)",
          backgroundColor: "rgba(34, 139, 34, 0.1)",
          tension: 0.4,
          fill: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            title: {
              display: true,
              text: "Wind Speed (m/s)"
            }
          },
          y: {
            title: {
              display: true,
              text: "Power (kW)"
            },
            beginAtZero: true
          }
        },
        plugins: {
          legend: {
            display: false
          }
        }
      }
    })
  }

  showForm(event) {
    event.preventDefault()
    this.formTarget.classList.remove("hidden")
  }

  hideForm(event) {
    event.preventDefault()
    this.formTarget.classList.add("hidden")
    this.windSpeedTarget.value = ""
    this.powerTarget.value = ""
  }

  async save(event) {
    event.preventDefault()

    const windSpeed = parseFloat(this.windSpeedTarget.value)
    const power = parseInt(this.powerTarget.value)

    if (!windSpeed || !power) {
      alert("Please fill in both fields")
      return
    }

    const formData = new FormData()
    formData.append("power_curve_point[wind_speed_mps]", windSpeed)
    formData.append("power_curve_point[power_kw]", power)

    try {
      const response = await fetch(`/admin/power_curve_profiles/${this.profileIdValue}/power_curve_points`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: formData
      })

      if (response.ok) {
        const data = await response.json()
        
        // Add new point to array and re-render
        this.pointsValue = [...this.pointsValue, { 
          id: data.id,
          wind_speed: windSpeed, 
          power: power 
        }]
        
        this.renderChart()
        this.addRowToTable(data.id, windSpeed, power)
        this.hideForm(event)
      } else {
        const error = await response.json()
        alert(error.message || "Error saving point")
      }
    } catch (error) {
      console.error("Error:", error)
      alert("Error saving point")
    }
  }

  addRowToTable(id, windSpeed, power) {
    const row = document.createElement("tr")
    row.className = "bg-white border-b border-gray-200 hover:bg-gray-50"
    row.innerHTML = `
      <td class="px-6 py-4 font-medium text-gray-900">${windSpeed}</td>
      <td class="px-6 py-4">${power}</td>
      <td class="px-6 py-4 text-right space-x-2">
        <a href="/admin/power_curve_profiles/${this.profileIdValue}/power_curve_points/${id}/edit" class="text-blue-600 hover:text-blue-800 transition">
          <svg class="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path></svg>
        </a>
        <a href="/admin/power_curve_profiles/${this.profileIdValue}/power_curve_points/${id}" data-turbo-method="delete" data-turbo-confirm="Are you sure?" class="text-red-600 hover:text-red-800 transition">
          <svg class="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>
        </a>
      </td>
    `
    this.tbodyTarget.appendChild(row)
  }

  async delete(event) {
    event.preventDefault()
    
    if (!confirm("Are you sure?")) return

    const button = event.currentTarget
    const url = button.href
    const row = button.closest("tr")

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        const pointId = parseInt(url.split("/").pop())
        this.pointsValue = this.pointsValue.filter(p => p.id !== pointId)
        this.renderChart()
        row.remove()
      } else {
        alert("Error deleting point")
      }
    } catch (error) {
      console.error("Error:", error)
      alert("Error deleting point")
    }
  }

  pointsValueChanged() {
    if (this.hasCanvasTarget) {
      this.renderChart()
    }
  }
}
